import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { useTheme } from './ThemeContext';
import axios from 'axios';
import { endpoint } from '../apiConfig';

const Login = () => {
  const { woodStyles } = useTheme();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const [email, setEmail] = useState('');
  const [tempCode, setTempCode] = useState('');
  const [code, setCode] = useState('');
  const [username, setUsername] = useState('');
  const [step, setStep] = useState('email');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(''), 3000);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Use the API endpoint here
      const response = await axios.post(endpoint('/api/request-code'), {
        email
      });

      if (response.status !== 200) {
        throw new Error(response.data?.error || 'Failed to send code');
      }

      setStep('code');
    } catch (err) {
      showError(err.message || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Use the API endpoint here
      const response = await axios.post(endpoint('/api/verify-code'), {
        code,
        email
      });

      const data = response.data;

      if (data.isExistingUser) {
        login({
          ...data,
          isAnonymous: false
        });
        navigate('/lobby');
      } else {
        setTempCode(data.tempCode);
        setStep('username');
      }
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'Failed to verify code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
  
    try {
      // Include email in the request
      const response = await axios.post(endpoint('/api/register-user'), {
        code: tempCode,
        username,
        email // Add the email parameter
      });
  
      const data = response.data;
      
      // Check for successful response
      if (response.status !== 200) {
        throw new Error(data.error || 'Registration failed');
      }
  
      login({
        ...data,
        isAnonymous: false
      });
  
      navigate('/lobby');
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Fallback to local-only mode if endpoint function returns null (offline mode)
  const handleContinueAsGuest = () => {
    const guestUser = {
      id: `guest_${Math.random().toString(36).substring(2, 9)}`,
      username: 'Guest',
      isAnonymous: true,
      isLocalOnly: true
    };
    
    localStorage.setItem('gameUser', JSON.stringify(guestUser));
    login(guestUser);
    navigate('/lobby');
  };

  return (
    <div
      className="max-h-screen flex items-center justify-center py-12 px-4"
      style={{
        background: woodStyles.background,
        backgroundImage: woodStyles.backgroundImage
      }}
    >
      <div
        className={`max-w-md w-full space-y-8 rounded-lg border-2 shadow-xl p-8 
          ${woodStyles.card.background} 
          ${woodStyles.card.border}`}
      >
        <div>
          <h2 className={`mt-2 text-center text-3xl font-extrabold ${woodStyles.text.primary}`}>
            {step === 'email' && 'Sign in to your account'}
            {step === 'code' && 'Enter verification code'}
            {step === 'username' && 'Choose your username'}
          </h2>
          <p className={`mt-2 text-center text-sm ${woodStyles.text.secondary}`}>
            Or{' '}
            <button 
              onClick={handleContinueAsGuest} 
              className="text-blue-500 hover:text-blue-400 cursor-pointer"
            >
              continue as guest
            </button>
          </p>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-md p-4">
            <div className={`text-sm text-red-500 ${woodStyles.text.primary}`}>
              {error}
            </div>
          </div>
        )}

        {step === 'email' && (
          <form onSubmit={handleEmailSubmit} className="mt-8 space-y-6">
            <div>
              <label
                htmlFor="email"
                className={`block text-sm font-medium ${woodStyles.text.primary}`}
              >
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={`mt-1 block w-full px-3 py-2 rounded-md shadow-sm
                  ${woodStyles.inlay.background}
                  ${woodStyles.text.primary}
                  ${woodStyles.card.border}
                  border focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="Enter your email"
                disabled={isLoading}
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white
                ${isLoading
                  ? woodStyles.button.disabled
                  : woodStyles.button.primary}
                transition-colors duration-200`}
            >
              {isLoading ? 'Sending code...' : 'Send login code'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={handleCodeSubmit} className="mt-8 space-y-6">
            <div>
              <label
                htmlFor="code"
                className={`block text-sm font-medium ${woodStyles.text.primary}`}
              >
                Verification Code
              </label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                className={`mt-1 block w-full px-3 py-2 rounded-md shadow-sm
                  ${woodStyles.inlay.background}
                  ${woodStyles.text.primary}
                  ${woodStyles.card.border}
                  border focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="Enter verification code"
              />
            </div>
            <div className="flex flex-col space-y-4">
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white
                  ${isLoading
                    ? woodStyles.button.disabled
                    : woodStyles.button.primary}
                  transition-colors duration-200`}
              >
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>
              <button
                type="button"
                onClick={() => setStep('email')}
                className={`text-sm ${woodStyles.text.secondary} hover:${woodStyles.text.primary}`}
              >
                Use different email
              </button>
            </div>
          </form>
        )}

        {step === 'username' && (
          <form onSubmit={handleUsernameSubmit} className="mt-8 space-y-6">
            <div>
              <label
                htmlFor="username"
                className={`block text-sm font-medium ${woodStyles.text.primary}`}
              >
                Choose Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className={`mt-1 block w-full px-3 py-2 rounded-md shadow-sm
                  ${woodStyles.inlay.background}
                  ${woodStyles.text.primary}
                  ${woodStyles.card.border}
                  border focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="Enter username"
                pattern="^[a-zA-Z0-9_]+$"
                minLength="3"
                maxLength="20"
              />
              <p className={`mt-1 text-sm ${woodStyles.text.secondary}`}>
                3-20 characters, letters, numbers, and underscores only
              </p>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white
                ${isLoading
                  ? woodStyles.button.disabled
                  : woodStyles.button.primary}
                transition-colors duration-200`}
            >
              {isLoading ? 'Creating account...' : 'Complete Registration'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;